const STORAGE_KEY = 'portal.postLoginRedirect'

const PUBLIC_PREFIXES = [
  '/login',
  '/forgot-password',
  '/reset-password',
  '/accept-invite',
  '/auth/google/callback',
  '/access-denied',
]

/** Survives React Strict Mode double-invoke within one login completion. */
let lastConsumedPath = undefined

/**
 * Allow only same-app relative paths (open-redirect safe).
 * @param {unknown} path
 * @returns {string|null}
 */
export function sanitizeInternalPath(path) {
  if (path == null) return null
  let value = String(path).trim()
  if (!value) return null
  try {
    value = decodeURIComponent(value)
  } catch {
    // keep raw
  }
  if (!value.startsWith('/')) return null
  if (value.startsWith('//')) return null
  if (/^[a-zA-Z][a-zA-Z+\-.]*:/.test(value)) return null
  const pathname = value.split(/[?#]/)[0] || ''
  if (PUBLIC_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))) {
    return null
  }
  return value
}

export function capturePostLoginRedirect(path) {
  const safe = sanitizeInternalPath(path)
  if (!safe) return false
  lastConsumedPath = undefined
  try {
    sessionStorage.setItem(STORAGE_KEY, safe)
    return true
  } catch {
    return false
  }
}

/**
 * Read and clear the stored post-login path.
 * Idempotent for a single login completion (Strict Mode safe).
 * @param {string} [fallback='/dashboard']
 */
export function consumePostLoginRedirect(fallback = '/dashboard') {
  if (lastConsumedPath !== undefined) {
    return lastConsumedPath || fallback
  }
  let stored = null
  try {
    stored = sessionStorage.getItem(STORAGE_KEY)
    sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    stored = null
  }
  const safe = sanitizeInternalPath(stored)
  lastConsumedPath = safe || fallback
  return lastConsumedPath
}

export function peekPostLoginRedirect() {
  try {
    return sanitizeInternalPath(sessionStorage.getItem(STORAGE_KEY))
  } catch {
    return null
  }
}

/** Build /login URL; optionally expose next= for debugging (sessionStorage is authoritative). */
export function buildLoginPath(nextPath, extraParams = {}) {
  const params = new URLSearchParams()
  Object.entries(extraParams).forEach(([key, value]) => {
    if (value != null && value !== '') params.set(key, String(value))
  })
  const safe = sanitizeInternalPath(nextPath)
  if (safe) params.set('next', safe)
  const query = params.toString()
  return query ? `/login?${query}` : '/login'
}

export function captureCurrentLocationForLogin() {
  if (typeof window === 'undefined') return false
  const path = `${window.location.pathname}${window.location.search}${window.location.hash}`
  return capturePostLoginRedirect(path)
}

/** Test helper */
export function __resetPostLoginRedirectForTests() {
  lastConsumedPath = undefined
  try {
    sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // ignore
  }
}
